import axios from "axios";
import { REFERENCE_EXITS } from "@/internals/constants/qrEditorErrorCodes";
import { logError, logWarning } from "@/services/DatadogService/DatadogService";

/**
 * Error raised when a file (image, video, audio, logo) cannot be prepared or uploaded
 * before the QR save request is sent. Carries a user-facing message and the original cause.
 */
export class QrUploadError extends Error {
  cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "QrUploadError";
    this.cause = cause;
  }
}

export const isQrUploadError = (error: unknown): error is QrUploadError =>
  error instanceof QrUploadError;

export type QrSavePhase = "prepare" | "upload" | "request";

const isResponseLike = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const candidate = error as { status?: unknown; response?: unknown; data?: unknown };
  return (
    typeof candidate.status === "number" ||
    (typeof candidate.response === "object" && candidate.response !== null) ||
    (typeof candidate.data === "object" && candidate.data !== null)
  );
};

/**
 * Classifies a save failure by the stage where it happened:
 * - "upload": file upload failure (QrUploadError)
 * - "request": HTTP request failure (AxiosError or response-like object)
 * - "prepare": anything else (payload conversion, fetch(blob), TypeError)
 */
export const classifyQrSaveError = (error: unknown): QrSavePhase => {
  if (isQrUploadError(error)) return "upload";
  if (axios.isAxiosError(error) || isResponseLike(error)) return "request";
  return "prepare";
};

/**
 * True when the form already shows the error inline, so no toast should be displayed:
 * duplicated reference or an invalid URL reported by the backend.
 */
export const isInlineHandledQrError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const candidate = error as {
    response?: { data?: unknown };
    data?: unknown;
  };
  const data = (candidate.response?.data ?? candidate.data) as
    { code?: unknown; valid?: unknown; errors?: Record<string, unknown[] | undefined> } | undefined;
  if (!data || typeof data !== "object") return false;

  if (data.code === REFERENCE_EXITS) return true;

  const urlErrors = data.errors?.["field-url"];
  return Array.isArray(urlErrors) && urlErrors.length > 0 && data.valid === false;
};

export interface QrSaveLogContext {
  qrType?: string;
  step?: number;
  isUpdate: boolean;
  isLoggedIn: boolean;
}

const toError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  return new Error(typeof error === "string" ? error : "Unknown QR save error");
};

/**
 * Logs a save failure with non-sensitive context only.
 * Never includes the QR payload or response data: only type, step, flags and phase.
 * HTTP failures are logged as warnings because the HTTP interceptor already logs details.
 * Never throws.
 */
export const logQrSaveFailure = (error: unknown, ctx: QrSaveLogContext): void => {
  try {
    const phase = classifyQrSaveError(error);
    const status =
      (error as { response?: { status?: unknown }; status?: unknown })?.response?.status ??
      (error as { status?: unknown })?.status;

    const context = {
      component: "useQR",
      action: "requestQR",
      phase,
      qrType: ctx.qrType,
      step: ctx.step,
      isUpdate: ctx.isUpdate,
      isLoggedIn: ctx.isLoggedIn,
      errorName: error instanceof Error ? error.name : typeof error,
      ...(typeof status === "number" ? { status } : {}),
    };

    if (phase === "request") {
      logWarning("QR save failed", context);
      return;
    }

    logError(toError(error), context);
  } catch {
    // Logging must never break the save flow.
  }
};
